import type { ActionTree, ActionContext } from 'vuex'
import type { RootState } from '@/store'
import type { Subscription } from '@/types/subscriptions'
import type { SubscriptionsState, EnrichedSubscription, UpdateSubscriptionsData } from './types'
import { SUBSCRIPTIONS_MUTATIONS, SUBSCRIPTIONS_ACTIONS, SUBSCRIPTIONS_GETTERS } from './types'
import {
  deviceIdToSerial,
  addOnSubscriptionsMock,
  deviceModels,
  tsiLinkSkus,
  genericTSILinkSubscriptionTiers,
  dataServicesSubscriptionTiers,
  subscriptionTiers,
  subscriptionNames,
  websiteBasicSubscriptionTiers,
  websitePremiumSubscriptionTiers,
  websiteSubscriptionTiers
} from './utils'

interface ISubscriptionsActions extends ActionTree<SubscriptionsState, RootState> {
  [SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS](
    context: ActionContext<SubscriptionsState, RootState>,
    data: UpdateSubscriptionsData
  ): Promise<void>
}

const actions: ISubscriptionsActions = {
  async [SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS]({ commit, getters }, data) {
    const { auth, api } = data

    //not authenticated, no subscriptions
    if (!auth.isAuthenticated) {
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_SUBSCRIPTIONS, [])
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_GENERIC_TSI_LINK, [])
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE, [])
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC, [])
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM, [])
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.BLUESKY
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.BLUESKY_GAS
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.AIRASSURE
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED, {
        qtyUsed: 0,
        model: deviceModels.BLUESKY
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED, {
        qtyUsed: 0,
        model: deviceModels.BLUESKY_GAS
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED, {
        qtyUsed: 0,
        model: deviceModels.AIRASSURE
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.BLUESKY
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.AIRASSURE
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.BLUESKY
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.BLUESKY_GAS
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING, {
        qtyRemaining: 0,
        model: deviceModels.AIRASSURE
      })
      commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_DATA_SERVICES, [])
      return
    }

    // only get skus if not already populated
    if (!getters[SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA]) {
      commit(
        SUBSCRIPTIONS_MUTATIONS.UPDATE_SKU_METADATA,
        await (await api.getSubscriptionSKUMetadata()).json()
      )
    }

    //calculate subscriptions
    const subRes = await api.getUserSubscriptions()
    const subscriptions: Subscription[] = await subRes.json()
    const sum = (accum: number, currentVal: number) => accum + currentVal
    const calcSubCounts = (sub: Subscription): EnrichedSubscription => {
      const enriched = sub as unknown as EnrichedSubscription
      let totalQty: number, totalQtyRemaining: number
      const used: number[] = []
      if (Array.isArray(sub.qty)) {
        totalQty = sub.qty.reduce(sum, 0)
      } else {
        totalQty = sub.qty
      }
      if (sub.qtyRemaining) {
        if (Array.isArray(sub.qtyRemaining)) {
          totalQtyRemaining = sub.qtyRemaining.reduce(sum, 0)
        } else {
          totalQtyRemaining = sub.qtyRemaining
        }
      } else {
        totalQtyRemaining = 0
      }
      const totalUsed = totalQty - totalQtyRemaining
      for (let i = 0; i < sub.qty.length; i++) {
        used[i] = sub.qty[i] - sub.qtyRemaining[i]
      }
      enriched.totalQty = totalQty
      enriched.totalQtyRemaining = totalQtyRemaining
      enriched.totalUsed = totalUsed
      enriched.used = used
      return enriched
    }

    const mapSubscriptionsToDeviceSerials = (
      subArray: EnrichedSubscription[],
      deviceSerialsMap: Record<string, string>
    ) => {
      for (const sub of subArray) {
        const flatIds = sub.deviceIds.flat()
        for (const id of flatIds) {
          if (id) {
            deviceSerialsMap[deviceIdToSerial(id)] = sub.id
          }
        }
      }
    }

    const addDefaultAddOnSubscriptionQtys = (genericSubs: EnrichedSubscription[]) => {
      if (genericSubs.length === 0) return

      const theRightSubs = genericSubs.find((sub) => {
        return (
          sub.id === subscriptionTiers.TSILINK_STANDARD || sub.id === subscriptionTiers.TSILINK_PLUS
        )
      })

      if (theRightSubs) {
        const skuNameFn = getters[SUBSCRIPTIONS_GETTERS.GET_SKU_METADATA_NAME_BY_ID] as (
          id: string
        ) => string
        const mockaddOnSubs = addOnSubscriptionsMock(skuNameFn)
        mockaddOnSubs.forEach((mockSub, index) => {
          const addOnSubIndex = genericSubs.findIndex((sub) => {
            return sub.id === mockSub.id
          })

          if (addOnSubIndex > -1) {
            genericSubs[addOnSubIndex].qty[0] += 1
            genericSubs[addOnSubIndex].qtyRemaining[0] += 1
            genericSubs[addOnSubIndex].totalQty += 1
            genericSubs[addOnSubIndex].totalQtyRemaining += 1
          } else {
            mockSub.sapAccountId = theRightSubs.sapAccountId
            mockSub.activationIds = [`${theRightSubs.activationIds[0]}${index}`]
            genericSubs.push(mockSub)
          }
        })
      }
    }

    const genericTSILinkSubscriptions: Subscription[] = []
    const dataServiceSubscriptions: Subscription[] = []
    const websiteSubscriptions: Subscription[] = []
    const basicSubscriptions: Subscription[] = []
    const premiumSubscriptions: Subscription[] = []
    let websiteQtyRemainingBlueSky = 0
    let websiteQtyRemainingBlueSkyGas = 0
    let websiteQtyRemainingAirAssure = 0
    let websiteQtyUsedBlueSky = 0
    let websiteQtyUsedBlueSkyGas = 0
    let websiteQtyUsedAirAssure = 0
    let basicQtyRemainingBlueSky = 0
    let basicQtyRemainingAirAssure = 0
    let premiumQtyRemainingBlueSky = 0
    let premiumQtyRemainingBlueSkyGas = 0
    let premiumQtyRemainingAirAssure = 0

    subscriptions.forEach((sub) => {
      if (
        sub.id.startsWith(tsiLinkSkus.GENERIC) ||
        sub.id.startsWith(deviceModels.BLUESKY) ||
        sub.id.startsWith(deviceModels.BLUESKY_GAS) ||
        sub.id.startsWith(deviceModels.AIRASSURE) ||
        sub.name == subscriptionNames.AirAssure ||
        sub.name == subscriptionNames.BlueSky
      ) {
        if ((genericTSILinkSubscriptionTiers as string[]).includes(sub.id)) {
          genericTSILinkSubscriptions.push(sub)
        }
        if ((dataServicesSubscriptionTiers as string[]).includes(sub.id)) {
          dataServiceSubscriptions.push(sub)
        }
        if ((websiteSubscriptionTiers as string[]).includes(sub.id)) {
          websiteSubscriptions.push(sub)
        }
        if ((websiteBasicSubscriptionTiers as string[]).includes(sub.id)) {
          basicSubscriptions.push(sub)
        }
        if ((websitePremiumSubscriptionTiers as string[]).includes(sub.id)) {
          premiumSubscriptions.push(sub)
        }
      }
    })

    // More complex logic will be required in the future to manage incrementing and decrementing user active subscriptions appropriately
    if (genericTSILinkSubscriptions.length) {
      for (const sub of genericTSILinkSubscriptions) {
        calcSubCounts(sub)
      }
    }

    if (websiteSubscriptions.length > 0) {
      for (const sub of websiteSubscriptions) {
        const enriched = calcSubCounts(sub)
        if (sub.id.startsWith(deviceModels.BLUESKY) || sub.name == subscriptionNames.BlueSky) {
          websiteQtyRemainingBlueSky += enriched.totalQtyRemaining
          websiteQtyUsedBlueSky += enriched.totalUsed
        } else if (
          sub.id.startsWith(deviceModels.BLUESKY_GAS) ||
          sub.name == subscriptionNames.BlueSkyGas
        ) {
          websiteQtyRemainingBlueSkyGas += enriched.totalQtyRemaining
          websiteQtyUsedBlueSkyGas += enriched.totalUsed
        } else {
          websiteQtyRemainingAirAssure += enriched.totalQtyRemaining
          websiteQtyUsedAirAssure += enriched.totalUsed
        }
      }
    }
    if (basicSubscriptions.length > 0) {
      for (const sub of basicSubscriptions) {
        const enriched = calcSubCounts(sub)
        if (sub.id.startsWith(deviceModels.BLUESKY) || sub.name == subscriptionNames.BlueSky) {
          basicQtyRemainingBlueSky += enriched.totalQtyRemaining
        } else {
          basicQtyRemainingAirAssure += enriched.totalQtyRemaining
        }
      }
    }
    if (premiumSubscriptions.length > 0) {
      for (const sub of premiumSubscriptions) {
        const enriched = calcSubCounts(sub)
        if (sub.id.startsWith(deviceModels.BLUESKY) || sub.name == subscriptionNames.BlueSky) {
          premiumQtyRemainingBlueSky += enriched.totalQtyRemaining
        } else if (
          sub.id.startsWith(deviceModels.BLUESKY_GAS) ||
          sub.name == subscriptionNames.BlueSkyGas
        ) {
          premiumQtyRemainingBlueSkyGas += enriched.totalQtyRemaining
        } else {
          premiumQtyRemainingAirAssure += enriched.totalQtyRemaining
        }
      }
    }
    if (dataServiceSubscriptions.length > 0) {
      for (const sub of dataServiceSubscriptions) {
        calcSubCounts(sub)
      }
    }
    const devicesSerialWebsite: Record<string, string> = {}
    const devicesSerialGeneric: Record<string, string> = {}
    const devicesSerialBasic: Record<string, string> = {}
    const devicesSerialPremium: Record<string, string> = {}
    const devicesSerialDataServices: Record<string, string> = {}

    // create a map of each device serial to the subscription it is associated with
    mapSubscriptionsToDeviceSerials(
      genericTSILinkSubscriptions as EnrichedSubscription[],
      devicesSerialGeneric
    )
    mapSubscriptionsToDeviceSerials(
      websiteSubscriptions as EnrichedSubscription[],
      devicesSerialWebsite
    )
    mapSubscriptionsToDeviceSerials(
      basicSubscriptions as EnrichedSubscription[],
      devicesSerialBasic
    )
    mapSubscriptionsToDeviceSerials(
      premiumSubscriptions as EnrichedSubscription[],
      devicesSerialPremium
    )
    mapSubscriptionsToDeviceSerials(
      dataServiceSubscriptions as EnrichedSubscription[],
      devicesSerialDataServices
    )

    // TODO: This is only for UI purposes. Will be removed when we implement the backend for this.
    addDefaultAddOnSubscriptionQtys(genericTSILinkSubscriptions as EnrichedSubscription[])

    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_SUBSCRIPTIONS, subscriptions)
    commit(
      SUBSCRIPTIONS_MUTATIONS.UPDATE_GENERIC_TSI_LINK,
      genericTSILinkSubscriptions as EnrichedSubscription[]
    )
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE, websiteSubscriptions as EnrichedSubscription[])
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC, basicSubscriptions as EnrichedSubscription[])
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM, premiumSubscriptions as EnrichedSubscription[])
    commit(
      SUBSCRIPTIONS_MUTATIONS.UPDATE_DATA_SERVICES,
      dataServiceSubscriptions as EnrichedSubscription[]
    )

    // update qtyRemaining for each website device type
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING, {
      qtyRemaining: websiteQtyRemainingBlueSky,
      model: deviceModels.BLUESKY
    })
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING, {
      qtyRemaining: websiteQtyRemainingBlueSkyGas,
      model: deviceModels.BLUESKY_GAS
    })
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING, {
      qtyRemaining: websiteQtyRemainingAirAssure,
      model: deviceModels.AIRASSURE
    })
    // update qtyUsed for each website device type
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED, {
      qtyUsed: websiteQtyUsedBlueSky,
      model: deviceModels.BLUESKY
    })
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED, {
      qtyUsed: websiteQtyUsedBlueSkyGas,
      model: deviceModels.BLUESKY_GAS
    })
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED, {
      qtyUsed: websiteQtyUsedAirAssure,
      model: deviceModels.AIRASSURE
    })
    // update qtyRemaining for each basic device type
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC_QTY_REMAINING, {
      qtyRemaining: basicQtyRemainingBlueSky,
      model: deviceModels.BLUESKY
    })
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC_QTY_REMAINING, {
      qtyRemaining: basicQtyRemainingAirAssure,
      model: deviceModels.AIRASSURE
    })
    // update qtyRemaining for each premium device type
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING, {
      qtyRemaining: premiumQtyRemainingBlueSky,
      model: deviceModels.BLUESKY
    })
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING, {
      qtyRemaining: premiumQtyRemainingBlueSkyGas,
      model: deviceModels.BLUESKY_GAS
    })
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING, {
      qtyRemaining: premiumQtyRemainingAirAssure,
      model: deviceModels.AIRASSURE
    })

    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_WEBSITE, devicesSerialWebsite)
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_GENERIC, devicesSerialGeneric)
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_BASIC, devicesSerialBasic)
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_PREMIUM, devicesSerialPremium)
    commit(SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_DATA_SERVICES, devicesSerialDataServices)
  }
}

export default actions
